import React from "react";
import { Link } from "react-router-dom";
import { TRIAL_DAYS } from "../HomeEssenziale/content";
import { useHomeLang } from "../HomeEssenziale/homeLang";
import "@fontsource-variable/newsreader/opsz.css";
import "@fontsource-variable/newsreader/opsz-italic.css";
import "./newspaper.css";

// The manifesto, set as the front page of a newspaper: the nameplate between
// its ears, the manifesto as the lead story under a headline across the
// page, and the rest of its chapters as the stories under it, in rows
// (newspaper.css).
//
// The homepage folds a copy of this page and unfolds it into the page
// itself (ManifestoSection.jsx), so the copy must be the page, word for
// word and box for box. `replica` makes it a picture of the page: no
// headings, no landmarks, no links, nothing a screen reader or the
// keyboard would find twice.
// The manifesto in each language, chapter by chapter, set the same way.
const PAPER = {
  it: {
    motto: "Davvero presente.",
    name: "Il Manifesto di A.V.R.A.H.",
    offer: `Prova gratis ${TRIAL_DAYS} giorni`,
    place: "Milano, 2026",
    edition: "Edizione unica",
    headline: "Più tecnologia, più umanità.",
    deck: "Come può un professionista mantenere la sua presenza costante su centinaia di clienti senza moltiplicare il proprio tempo?",
    problem: "Il problema",
    problemText: <>
      <p>Negli ultimi anni abbiamo reso quasi tutto self-service. Possiamo cercare e ottenere informazioni e, oggi, chiedere a un’intelligenza artificiale una risposta su praticamente qualsiasi argomento.</p>
      <p><b>È stato un enorme progresso.</b> Ma c’è un effetto collaterale: sempre più spesso, anche quando la decisione è importante, il peso di capire, scegliere e controllare ricade interamente sulla persona.</p>
      <p>Nella consulenza finanziaria e assicurativa questo tema diventa particolarmente evidente. Il cliente non cerca soltanto informazioni. Cerca qualcuno che conosca la sua storia, sappia interpretare ciò che accade, lo aiuti a scegliere e metta la propria responsabilità professionale dietro ciò che gli sta proponendo.</p>
      <p>Il problema è che un consulente può seguire centinaia di persone. Può incontrarle una volta, due volte, qualche volta in più durante l’anno. Poi rimangono telefonate, e-mail, WhatsApp e messaggi vocali, tutti strumenti utili ma immersi nello stesso rumore informativo che accompagna ormai ogni giornata.</p>
      <p>Da qui nasce la domanda alla base di A.V.R.A.H.: <b>come può un professionista mantenere la sua presenza costante su centinaia di clienti senza moltiplicare il proprio tempo?</b></p>
    </>,
    now: "Perché adesso",
    nowText: <>
      <p>Per molto tempo una parte importante del valore del consulente derivava anche dall’accesso all’informazione. Oggi l’informazione è ovunque. Con l’intelligenza artificiale diventerà sempre più abbondante, veloce e facilmente accessibile.</p>
      <p>Il valore del consulente, allora, si sposta. Lo si sceglie perché conosce il cliente. Perché distingue ciò che conta dal rumore. Perché interpreta. Perché si assume una responsabilità. Perché dietro una decisione importante rimane riconoscibile una persona reale.</p>
      <p><i>È questa la convinzione da cui nasce A.V.R.A.H.</i></p>
    </>,
    what: "Che cos’è A.V.R.A.H.",
    whatText: <>
      <p><b>Le piattaforme ti collegano. A.V.R.A.H. ti fa arrivare.</b> Le piattaforme di streaming hanno risolto il problema del collegamento. Noi stiamo risolvendo quello che viene dopo.</p>
      <p>Quanto di una persona riesce davvero ad attraversare uno schermo? A.V.R.A.H. nasce per aumentare quella quantità.</p>
      <p>Incontrarsi a distanza è ormai una consuetudine. La sfida è che l’incontro digitale resti umano e riconoscibile: al centro c’è la persona, accompagnata dai suoi contenuti. E la forza del video rende il messaggio più professionale e più incisivo.</p>
      <p>Un consulente può anche registrare in pochi minuti un breve messaggio personale, spiegare un documento, commentare un evento, preparare un incontro o introdurre uno specialista, lasciando al cliente la possibilità di guardarlo quando preferisce.</p>
    </>,
    async: "Live e asincrono",
    asyncText: <>
      <p>L’incontro personale conserva un valore enorme. Così come la telefonata nel momento giusto. A.V.R.A.H. non vuole sostituirli: vuole coprire quello spazio enorme che esiste tra un incontro e l’altro.</p>
      <p>Il cliente non deve trovare un’ora libera. Il consulente non deve organizzare ogni volta una nuova riunione. E soprattutto non deve diventare regista, montatore o videomaker. Deve continuare a fare il consulente.</p>
      <p><i>Al resto pensa la tecnologia.</i></p>
    </>,
    ai: "Intelligenza artificiale",
    aiText: <>
      <p><b>L’AI lavora per te. Non al posto tuo.</b> Si può usare l’intelligenza artificiale per rendere una persona più forte, o per sostituirla con una copia. Sono due strade opposte. A.V.R.A.H. ha scelto la prima: la regia, la scena e il montaggio li fa il software; tu comunichi a tutti, in modo semplice e veloce; il tempo risparmiato torna ai tuoi clienti.</p>
      <blockquote className="np-quote">«Il valore resta nel volto, nella voce, nel giudizio e nella responsabilità di una persona reale.»</blockquote>
    </>,
    origins: "Le origini",
    originsText: <>
      <p>A.V.R.A.H. è nato dall’incontro tra due mondi, e tra le persone che li hanno vissuti: chi ha studiato l’intelligenza artificiale a Stanford, nel cuore della Silicon Valley, e chi alla consulenza ha dedicato una vita, con passione e rispetto per questo mestiere. Per noi l’AI non è una moda. È una scelta consapevole.</p>
    </>,
    europe: "Tecnologia europea",
    europeText: <>
      <p>A.V.R.A.H. è sviluppato in Italia, in Europa, ed è stato progettato con un’architettura local-first: il software lavora sul computer dell’utilizzatore e non richiede che documenti e registrazioni transitino necessariamente attraverso un cloud proprietario A.V.R.A.H.</p>
      <p>Per banche, assicurazioni, reti e professionisti che trattano informazioni riservate, avere maggiore controllo sul proprio ambiente digitale rappresenta una scelta sempre più rilevante.</p>
      <p>La stessa logica guida lo sviluppo della Compliance by Design: portare alcuni presidi definiti dall’organizzazione direttamente dentro il processo quotidiano di comunicazione, senza sostituire né le funzioni Compliance né la responsabilità del professionista.</p>
    </>,
  },
  en: {
    motto: "Really there.",
    name: "The A.V.R.A.H. Manifesto",
    offer: `Try it free for ${TRIAL_DAYS} days`,
    place: "Milan, 2026",
    edition: "Single edition",
    headline: "More technology, more humanity.",
    deck: "How can a professional stay present for hundreds of clients without multiplying their own time?",
    problem: "The problem",
    problemText: <>
      <p>In recent years we have made almost everything self-service. We can look up and get information, and today we can ask an artificial intelligence for an answer on practically any subject.</p>
      <p><b>It has been enormous progress.</b> But there is a side effect: more and more often, even when the decision matters, the burden of understanding, choosing and checking falls entirely on the individual.</p>
      <p>In financial and insurance advice this becomes especially clear. Clients aren’t only looking for information. They are looking for someone who knows their story, can make sense of what is happening, helps them choose, and puts their own professional responsibility behind what they recommend.</p>
      <p>The problem is that an advisor may look after hundreds of people. They can meet them once, twice, a few more times a year. After that come calls, emails, WhatsApp chats and voice notes: all useful, and all lost in the same noise that now fills every day.</p>
      <p>That is where the question behind A.V.R.A.H. comes from: <b>how can a professional stay present for hundreds of clients without multiplying their own time?</b></p>
    </>,
    now: "Why now",
    nowText: <>
      <p>For a long time, much of an advisor’s value also came from access to information. Today information is everywhere. With artificial intelligence it will only become more abundant, faster and easier to reach.</p>
      <p>So the advisor’s value shifts. Clients choose an advisor because they know them. Because they can tell what matters from the noise. Because they interpret. Because they take responsibility. Because behind an important decision there is still a real person you can recognize.</p>
      <p><i>That is the conviction A.V.R.A.H. was born from.</i></p>
    </>,
    what: "What A.V.R.A.H. is",
    whatText: <>
      <p><b>Platforms connect you. A.V.R.A.H. gets you across.</b> Streaming platforms solved the problem of connecting. We are solving what comes after.</p>
      <p>How much of a person really makes it through a screen? A.V.R.A.H. exists to make that more.</p>
      <p>Meeting remotely is now routine. The challenge is keeping the digital meeting human and recognizable: the person at the center, with their content beside them. And the strength of video makes the message more professional and more compelling.</p>
      <p>An advisor can also record a short personal message in a few minutes, explain a document, comment on an event, prepare for a meeting or introduce a specialist, and let the client watch it whenever suits them.</p>
    </>,
    async: "Live and asynchronous",
    asyncText: <>
      <p>Meeting in person keeps enormous value. So does the phone call at the right moment. A.V.R.A.H. doesn’t aim to replace them: it aims to fill the huge space between one meeting and the next.</p>
      <p>The client doesn’t have to find a free hour. The advisor doesn’t have to set up a new meeting every time. Above all, they don’t have to become a director, an editor or a videographer. They get to keep being an advisor.</p>
      <p><i>Technology takes care of the rest.</i></p>
    </>,
    ai: "Artificial intelligence",
    aiText: <>
      <p><b>AI works for you. Not instead of you.</b> Artificial intelligence can make a person stronger, or replace them with a copy. Those are opposite roads. A.V.R.A.H. chose the first: the software handles directing, staging and editing; you reach everyone, simply and quickly; and the time you save goes back to your clients.</p>
      <blockquote className="np-quote">“The value stays in the face, the voice, the judgment and the responsibility of a real person.”</blockquote>
    </>,
    origins: "Origins",
    originsText: <>
      <p>A.V.R.A.H. was born where two worlds met, and the people who lived them: one who studied artificial intelligence at Stanford, in the heart of Silicon Valley, and one who gave a lifetime to financial advice, with passion and respect for the craft. For us, AI isn’t a trend. It’s a deliberate choice.</p>
    </>,
    europe: "European technology",
    europeText: <>
      <p>A.V.R.A.H. is built in Italy, in Europe, on a local-first architecture: the software runs on the user’s own computer, and documents and recordings don’t have to pass through a proprietary A.V.R.A.H. cloud.</p>
      <p>For banks, insurers, advisor networks and professionals who handle confidential information, having more control over their own digital environment is an increasingly important choice.</p>
      <p>The same thinking drives Compliance by Design: bringing safeguards the organization defines directly into everyday communication, without replacing the Compliance function or the professional’s own responsibility.</p>
    </>,
  },
};

export default function Newspaper({ replica = false }) {
  const lang = useHomeLang();
  const paper = PAPER[lang];
  const Headline = replica ? "p" : "h1";
  const Heading = replica ? "p" : "h2";
  const Story = replica ? "div" : "section";
  const Offer = replica ? "span" : Link;
  const story = (title, className, children) => (
    <Story className={`np-story ${className}`}>
      <Heading className="np-title">{title}</Heading>
      <div className="np-text">{children}</div>
    </Story>
  );

  return (
    <article className="np" lang={lang}>
      <div className="np-in hf-wrap">
        <header className="np-mast">
          {/* The paper's motto and its one advertisement, in its ears. */}
          <p className="np-ear">{paper.motto}</p>
          <p className="np-name">{paper.name}</p>
          <Offer className="np-ear np-offer" {...(replica ? {} : { to: "/signup" })}>
            {paper.offer}
          </Offer>
        </header>
        <p className="np-date"><span>{paper.place}</span><span>{paper.edition}</span></p>

        <div className="np-lead">
          <Headline className="np-head">{paper.headline}</Headline>
          <p className="np-deck">{paper.deck}</p>
          {/* The lead story runs the first two chapters on, each under a
              head set into the column, as a long piece is broken up. */}
          <div className="np-text np-body">
            <Heading className="np-sub">{paper.problem}</Heading>
            {paper.problemText}
            <Heading className="np-sub">{paper.now}</Heading>
            {paper.nowText}
          </div>
        </div>

        <div className="np-row">
          {story(paper.what, "np-s3", paper.whatText)}
          {story(paper.async, "np-s4", paper.asyncText)}
        </div>

        {/* The shorter stories run on one after another through the
            columns, as a paper runs its briefs. */}
        <div className="np-row np-flow">
          {story(paper.ai, "np-s5", paper.aiText)}
          {story(paper.origins, "np-s6", paper.originsText)}
          {story(paper.europe, "np-s7", paper.europeText)}
        </div>
      </div>
    </article>
  );
}
